/**
 * Dashboard Component - Main data visualization
 * Shows statistics, charts, and interactive district map
 */

import { ApiService, SummaryStats, DistrictStats } from '@/api/ApiService';
import { StorageService } from '@/utils/StorageService';
import { StatsCards } from '@/components/StatsCards';
import { DistrictMap } from '@/components/DistrictMap';
import { TrendChart } from '@/components/TrendChart';

export interface DashboardData {
  stats: SummaryStats;
  districts: DistrictStats[];
}

export class Dashboard {
  private container: HTMLElement | null = null;
  private statsCards: StatsCards;
  private districtMap: DistrictMap;
  private trendChart: TrendChart;
  private currentData: DashboardData | null = null;
  private selectedDistrict: string | null = null;

  constructor(
    private apiService: ApiService,
    private storageService: StorageService
  ) {
    this.statsCards = new StatsCards();
    this.districtMap = new DistrictMap();
    this.trendChart = new TrendChart();

    this.setupEventListeners();
  }

  render(container: HTMLElement): void {
    this.container = container;
    
    container.innerHTML = `
      <div class="dashboard">        
        <!-- Summary Statistics Cards -->
        <div class="dashboard-section">
          <div id="stats-cards"></div>
        </div>
        
        <!-- Interactive Map -->
        <div class="dashboard-section">
          <div class="section-header">
            <h2 class="section-title">Outbreak Map</h2>
            <p class="section-subtitle">
              Interactive map showing mpox cases across Sierra Leone's 16 districts
            </p>
          </div>
          <div id="district-map"></div>
        </div>
        
        <!-- Trend Chart -->
        <div class="dashboard-section">
          <div class="section-header">
            <h2 class="section-title">7-Day Trend</h2>
            <p class="section-subtitle">
              Track daily changes in cases, deaths, and vaccinations
            </p>
          </div>
          <div id="trend-chart"></div>
        </div>
        
        <!-- Additional Information -->
        <div class="dashboard-section">
          <div class="info-grid">
            <div class="info-card">
              <h3>💉 Get Vaccinated</h3>
              <p>
                Vaccination is available at all district hospitals and selected 
                health centers. Contact your nearest health facility for availability.
              </p>
              <button class="btn btn-primary" id="find-vaccines">
                Find Vaccine Sites
              </button>
            </div>
            
            <div class="info-card">
              <h3>🏥 Symptoms</h3>
              <p>
                Watch for fever, headache, muscle aches, and skin rash. 
                Seek medical attention immediately if you have these symptoms.
              </p>
              <button class="btn btn-outline" id="learn-symptoms">
                Learn More
              </button>
            </div>
            
            <div class="info-card">
              <h3>📞 Emergency</h3>
              <p>
                For medical emergencies or to report suspected cases, 
                contact the national emergency hotline immediately.
              </p>
              <a href="tel:117" class="btn btn-danger">
                Call 117
              </a>
            </div>
          </div>
        </div>
      </div>
    `;

    this.renderComponents();
    this.addStyles();
  }

  private renderComponents(): void {
    if (!this.container) return;

    // Render stats cards
    const statsElement = this.container.querySelector('#stats-cards');
    if (statsElement) {
      this.statsCards.render(statsElement as HTMLElement);
    }

    // Render map
    const mapElement = this.container.querySelector('#district-map');
    if (mapElement) {
      this.districtMap.render(mapElement as HTMLElement);
    }

    // Render chart
    const chartElement = this.container.querySelector('#trend-chart');
    if (chartElement) {
      this.trendChart.render(chartElement as HTMLElement);
    }
  }

  private setupEventListeners(): void {
    // Listen for district selection
    document.addEventListener('district:select', (event: CustomEvent) => {
      this.handleDistrictSelect(event.detail.districtCode);
    });

    // Listen for map interactions
    document.addEventListener('map:district-click', (event: CustomEvent) => {
      this.handleDistrictSelect(event.detail.districtCode);
    });

    // Handle action buttons
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      
      if (target.id === 'find-vaccines') {
        this.showVaccineSites();
      } else if (target.id === 'learn-symptoms') {
        this.showSymptomInfo();
      }
    });
  }

  async updateData(data: DashboardData): Promise<void> {
    this.currentData = data;
    
    try {
      // Update all components with new data
      await Promise.all([
        this.statsCards.updateData(data.stats, this.getSelectedDistrictStats()),
        this.districtMap.updateData(data.districts),
        this.trendChart.updateData(data.districts, this.selectedDistrict)
      ]);

      console.log('Dashboard updated with fresh data');
    } catch (error) {
      console.error('Failed to update dashboard:', error);
    }
  }

  selectDistrict(districtCode: string): void {
    this.selectedDistrict = districtCode;
    
    if (this.currentData) {
      const districtStats = this.getSelectedDistrictStats();
      
      // Update components that show district-specific data
      this.statsCards.updateData(this.currentData.stats, districtStats);
      this.trendChart.updateData(this.currentData.districts, districtCode);
      this.districtMap.selectDistrict(districtCode);
    }
  }

  private handleDistrictSelect(districtCode: string): void {
    if (districtCode === this.selectedDistrict) return;
    
    this.selectDistrict(districtCode);
    
    // Save user preference
    this.storageService.savePreferences({ selectedDistrict: districtCode });
    
    // Dispatch event for other components
    const event = new CustomEvent('district:selected', {
      detail: { districtCode }
    });
    document.dispatchEvent(event);
  }

  private getSelectedDistrictStats(): DistrictStats | null {
    if (!this.currentData || !this.selectedDistrict) return null;
    
    return this.currentData.districts.find(
      d => d.district_code === this.selectedDistrict
    ) || null;
  }

  showLoading(): void {
    // Show loading state in all components
    this.statsCards.showLoading();
    this.districtMap.showLoading();
    this.trendChart.showLoading();
  }

  private showVaccineSites(): void {
    // Create modal showing vaccine sites
    const modal = document.createElement('div');
    modal.className = 'modal-overlay';
    modal.innerHTML = `
      <div class="modal-content">
        <div class="modal-header">
          <h2>💉 Vaccine Sites</h2>
          <button class="modal-close">×</button>
        </div>
        <div class="modal-body">
          <div class="vaccine-info">
            <h3>Where to Get Vaccinated</h3>
            <p>
              Mpox vaccines are available at government hospitals and selected 
              health centers across all 16 districts in Sierra Leone.
            </p>
            
            <h4>Government Hospitals</h4>
            <ul>
              <li>Connaught Hospital (Western Area Urban)</li>
              <li>Bo Government Hospital (Bo District)</li>
              <li>Kenema Government Hospital (Kenema District)</li>
              <li>Makeni Government Hospital (Bombali District)</li>
            </ul>
            
            <h4>What to Bring</h4>
            <ul>
              <li>National ID or other identification</li>
              <li>Previous vaccination records (if available)</li>
            </ul>
            
            <h4>Operating Hours</h4>
            <p>
              Monday - Friday: 8:00 AM - 4:00 PM<br>
              Saturday: 8:00 AM - 12:00 PM<br>
              Emergency: 24/7 at major hospitals
            </p>
            
            <div class="contact-info">
              <p>
                <strong>For more information:</strong><br>
                Call your district health office or the national hotline at <strong>117</strong>
              </p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-primary modal-close">Close</button>
        </div>
      </div>
    `;

    this.showModal(modal);
  }

  private showSymptomInfo(): void {
    // Create modal showing symptom information
    const modal = document.createElement('div');
    modal.className = 'modal-overlay';
    modal.innerHTML = `
      <div class="modal-content">
        <div class="modal-header">
          <h2>🏥 Mpox Symptoms</h2>
          <button class="modal-close">×</button>
        </div>
        <div class="modal-body">
          <div class="symptom-info">
            <h3>Early Symptoms (1-3 days)</h3>
            <ul>
              <li>🌡️ Fever and chills</li>
              <li>🤕 Headache and muscle aches</li>
              <li>😴 Fatigue and exhaustion</li>
              <li>🤢 Nausea and loss of appetite</li>
            </ul>
            
            <h3>Characteristic Symptoms (4-21 days)</h3>
            <ul>
              <li>🔴 Skin rash or lesions</li>
              <li>💧 Fluid-filled bumps</li>
              <li>🩹 Scabs that eventually fall off</li>
            </ul>
            
            <div class="alert alert-danger">
              <strong>⚠️ Seek Immediate Medical Attention If:</strong>
              <ul>
                <li>You develop a rash or skin lesions</li>
                <li>You have fever with other symptoms</li>
                <li>You've been in contact with a confirmed case</li>
                <li>You're experiencing severe symptoms</li>
              </ul>
            </div>
            
            <h3>Prevention</h3>
            <ul>
              <li>🧼 Wash hands frequently with soap</li>
              <li>😷 Avoid close contact with sick people</li>
              <li>🧤 Use personal protective equipment</li>
              <li>💉 Get vaccinated if eligible</li>
            </ul>
            
            <div class="contact-info">
              <p>
                <strong>Emergency:</strong> Call 117<br>
                <strong>For non-emergencies:</strong> Contact your nearest health facility
              </p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-primary modal-close">Close</button>
        </div>
      </div>
    `;

    this.showModal(modal);
  }

  private showModal(modal: HTMLElement): void {
    document.body.appendChild(modal);

    // Setup close handlers
    const closeButtons = modal.querySelectorAll('.modal-close');
    const closeModal = () => {
      modal.remove();
      document.body.style.overflow = '';
    };

    closeButtons.forEach(btn => {
      btn.addEventListener('click', closeModal);
    });

    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Focus management
    const firstButton = modal.querySelector('button');
    firstButton?.focus();
  }

  private addStyles(): void {
    const style = document.createElement('style');
    style.textContent = `
      .dashboard {
        display: flex;
        flex-direction: column;
        gap: var(--space-8);
      }
      
      .dashboard-section {
        width: 100%;
        margin-bottom: var(--space-8);
      }
      
      .section-header {
        margin-bottom: var(--space-6);
        text-align: center;
      }
      
      .section-title {
        font-size: var(--text-2xl);
        font-weight: 700;
        color: var(--primary-green);
        margin-bottom: var(--space-2);
      }
      
      .section-subtitle {
        font-size: var(--text-base);
        color: var(--gray-600);
        max-width: 600px;
        margin: 0 auto;
      }
      
      .info-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: var(--space-6);
      }
      
      @media (min-width: 768px) {
        .info-grid {
          grid-template-columns: repeat(3, 1fr);
        }
      }
      
      .info-card {
        background: white;
        border-radius: var(--radius-lg);
        box-shadow: var(--shadow);
        padding: var(--space-6);
        text-align: center;
        transition: var(--transition-base);
      }
      
      .info-card:hover {
        box-shadow: var(--shadow-md);
        transform: translateY(-2px);
      }
      
      .info-card h3 {
        font-size: var(--text-lg);
        font-weight: 600;
        color: var(--primary-green);
        margin-bottom: var(--space-3);
      }
      
      .info-card p {
        color: var(--gray-600);
        margin-bottom: var(--space-4);
        line-height: 1.6;
      }
      
      .vaccine-info h4 {
        color: var(--primary-green);
        margin: var(--space-4) 0 var(--space-2);
      }
      
      .vaccine-info ul,
      .symptom-info ul {
        text-align: left;
        margin: var(--space-2) 0 var(--space-4);
      }
      
      .vaccine-info li,
      .symptom-info li {
        margin-bottom: var(--space-1);
      }
      
      .symptom-info h3 {
        color: var(--primary-green);
        margin: var(--space-4) 0 var(--space-2);
        border-bottom: 2px solid var(--primary-green);
        padding-bottom: var(--space-1);
      }
      
      .contact-info {
        background: var(--gray-50);
        padding: var(--space-4);
        border-radius: var(--radius);
        margin-top: var(--space-4);
        text-align: center;
      }
    `;
    
    document.head.appendChild(style);
  }
}